import { groupBy, mapBy } from '@go-shared/lib/functional';
import {
  buildCalendarMarkers,
  buildDateOption,
  buildReserveSlotKey,
  buildTicketSelections,
  DEFAULT_RESOURCE_KEY,
  timeSorter,
} from '@go-shared/lib/reserve';
import {
  addMinutesToTime,
  calculateTimeDifference,
  dateToIsoUTC,
  dateToIsoLocal,
  isoLocal,
  formatTimeRange,
} from '@go-shared/lib/time';
import { makeFullName } from '@go-shared/lib/utils';
import {
  ReservableEventTemplateSnapshot,
  ReservableEventWithTimeslots,
} from '@go-shared/types/reservable';
import { ReserveScheduleSlotPriceOverrides } from '@go-shared/types/reserveSchedule';
import { User } from '@go-shared/types/user';
import React, { useState } from 'react';
import {
  BubbleAlert,
  BubbleButton,
  BubbleCalendar,
  BubbleCheckbox,
  BubbleFormGroup,
  BubbleHintSelect,
  BubbleInput,
  BubbleOverlay,
} from './BubbleUI';
import { ReservationFormikOccurrence } from './Formik/ReservationFormik';

export interface OccurrenceFillerProps {
  values: ReservationFormikOccurrence;
  onChange: (next: ReservationFormikOccurrence) => void;
  onSetPriceOverrides?: (priceOverrides?: ReserveScheduleSlotPriceOverrides) => void;
  onClear?: () => void;
  ticketQuantities: Array<{ ticket: { _id: string }; quantity: number }>;
  users: User[];
  eventSnapshot: ReservableEventTemplateSnapshot;
  reservableDetails?: ReservableEventWithTimeslots;
  reservableDetailsLoading?: boolean;
  readOnly?: boolean;
}

export default function OccurrenceFiller(props: OccurrenceFillerProps) {
  const {
    values,
    onChange,
    onSetPriceOverrides,
    ticketQuantities,
    users,
    eventSnapshot,
    reservableDetails,
    reservableDetailsLoading,
    readOnly,
    onClear,
  } = props;
  const [advancedMode, setAdvancedMode] = useState(false);

  const resourcesList = eventSnapshot.reservables.tickets;
  const usersMap = mapBy(users, u => u._id);
  const resourcesMap = mapBy(resourcesList, r => r._id);
  const ticketsMap = mapBy(resourcesList, t => t._id);
  const isResource = eventSnapshot.reservables.experienceType === 'resource';
  const timeslotsByDate = groupBy(reservableDetails?.timeslots ?? [], t => dateToIsoUTC(t.date));

  const selectedDate = buildDateOption(
    values.date
      ? values.date
      : reservableDetails?.timeslots[0]?.date
        ? dateToIsoUTC(reservableDetails?.timeslots[0]?.date)
        : isoLocal(),
    timeslotsByDate
  );

  const allTimesFromSelectedDate = timeslotsByDate[selectedDate.key] ?? [];

  const selectedSlotKey = values.time
    ? buildReserveSlotKey(
        { date: values.date, resource: values.resource, host: values.host },
        values.time,
        values.duration
      )
    : undefined;

  const selectedDateTimesByResource = groupBy(
    allTimesFromSelectedDate,
    t => t.resource || DEFAULT_RESOURCE_KEY
  );

  return (
    <div className="grid grid-cols-1 gap-6">
      <div className="flex items-start">
        <BubbleFormGroup label="Date" className="shrink-0 pr-4" style={{ width: 450 }}>
          <BubbleOverlay loading={reservableDetailsLoading}>
            <BubbleCalendar
              responsive
              readOnly={readOnly}
              selectedDate={selectedDate.date}
              onDateSelect={date => onChange({ ...values, date: dateToIsoLocal(date) })}
              addMarkers={dayContext =>
                buildCalendarMarkers(
                  dayContext.dateIso,
                  timeslotsByDate,
                  buildTicketSelections(eventSnapshot.reservables.tickets, ticketQuantities)
                    .quantities,
                  values.time
                    ? { time: values.time, duration: values.duration, host: values.host }
                    : undefined,
                  values.resource
                )
              }
              showClear
              showToday
              onClear={onClear}
              subtitle={() => (
                <div className="text-xs text-secondary">
                  <div className="flex items-center gap-1">
                    <span
                      className="inline-block rounded-full"
                      style={{ width: 8, height: 8, backgroundColor: '#22c55e' }}
                    />
                    Available for your time slot and party size
                  </div>
                  <div className="flex items-center gap-1">
                    <span
                      className="inline-block rounded-full"
                      style={{ width: 8, height: 8, backgroundColor: '#f59e0b' }}
                    />
                    Available, but not for your time slot or party size
                  </div>
                  <div className="flex items-center gap-1">
                    <span
                      className="inline-block rounded-full"
                      style={{ width: 8, height: 8, backgroundColor: '#ef4444' }}
                    />
                    No availability for this date
                  </div>
                </div>
              )}
            />
          </BubbleOverlay>
        </BubbleFormGroup>
        <div style={{ width: 'calc(100% - 450px)' }} className="shrink-0 flex flex-col gap-4">
          {allTimesFromSelectedDate.length > 0 ? (
            <div className="overflow-x-auto">
              <div className="flex gap-4 mb-4">
                {Object.entries(selectedDateTimesByResource).map(([resource, times]) => (
                  <BubbleFormGroup
                    key={resource}
                    label={
                      isResource ? (ticketsMap[resource]?.name ?? 'Unknown Rental') : 'Time Slots'
                    }
                    className="shrink-0"
                    style={{ width: 200 }}
                  >
                    <div className="flex flex-col gap-2">
                      {times.sort(timeSorter).map(item => {
                        const isSelected = selectedSlotKey === item.slotKey;
                        const timeLabel = formatTimeRange(item.time, item.duration);
                        const hostLabel = item.host
                          ? makeFullName(
                              usersMap[item.host]?.firstName,
                              usersMap[item.host]?.lastName
                            )
                          : undefined;

                        const selectedVariant = item.spotsLeft > 0 ? 'navy' : 'smoke';
                        const selectedTextVariant =
                          item.spotsLeft > 0 ? '' : isSelected ? 'white' : 'smoke';
                        const priceOverrideKeys = Object.keys(item.priceOverrides || {});

                        return (
                          <BubbleButton
                            key={item._id}
                            block
                            className={`border border-${selectedVariant} ${item.spotsLeft === 0 ? 'italic' : ''}`}
                            variant={isSelected ? selectedVariant : 'white'}
                            onClick={() => {
                              onChange({
                                ...values,
                                date: dateToIsoUTC(item.date),
                                time: item.time,
                                duration: item.duration,
                                host: item.host,
                                resource: item.resource,
                              });
                              onSetPriceOverrides?.(item.priceOverrides || {});
                            }}
                          >
                            <div className="flex items-center">
                              <div className="text-left mr-3">
                                {item.label && (
                                  <div className={`text-${selectedTextVariant}`}>{item.label}</div>
                                )}
                                <div
                                  className={`${
                                    item.spotsLeft === 0 ? `text-${selectedTextVariant}` : ''
                                  } ${item.label ? `text-sm ${isSelected ? 'text-eggshell' : item.spotsLeft !== 0 ? 'text-slate' : ''}` : ''}`}
                                >
                                  {timeLabel}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center">
                              {hostLabel && (
                                <div
                                  className={`text-sm mr-3 ${isSelected ? 'text-eggshell-darkest' : 'text-smoke'}`}
                                >
                                  {hostLabel}
                                </div>
                              )}
                              <div className={`ml-auto text-${selectedTextVariant}`}>
                                {item.spotsLeft} / {item.capacity}
                              </div>
                            </div>
                            <div className="flex items-center">
                              {priceOverrideKeys.length > 0 && (
                                <div
                                  className={`text-sm mt-1 ${isSelected ? 'text-eggshell-darkest' : 'text-smoke'}`}
                                >
                                  + price overrides
                                </div>
                              )}
                            </div>
                          </BubbleButton>
                        );
                      })}
                    </div>
                  </BubbleFormGroup>
                ))}
              </div>
            </div>
          ) : (
            <BubbleFormGroup label="Time Slots">
              <BubbleAlert variant="banana">No timeslots are available for this date.</BubbleAlert>
            </BubbleFormGroup>
          )}
          <div className="text-smoke italic">
            Slot availability takes into account guest's carts as well as active reservations.
          </div>
        </div>
      </div>
      <div>
        <BubbleCheckbox
          label="Edit Manually (Custom Schedule)"
          checked={advancedMode}
          onValue={setAdvancedMode}
          subtitle="Allows you to set a custom date, time, host, and resource. Use with caution, as this can create reservations that don't align with available timeslots. Unticking this will not reset any of the custom values you've entered."
        />
        {advancedMode && (
          <fieldset className="grid grid-cols-6 gap-4 mt-4">
            <BubbleFormGroup label="Date" className="col-span-2">
              <BubbleInput
                block
                type="date"
                readOnly={readOnly}
                value={values.date}
                onValue={val => onChange({ ...values, date: val })}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Start Time" className="col-span-2">
              <BubbleInput
                block
                type="time"
                readOnly={readOnly}
                value={values.time}
                onValue={val => onChange({ ...values, time: val })}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="End Time" className="col-span-2">
              <BubbleInput
                type="time"
                block
                readOnly={readOnly}
                value={
                  !values.time
                    ? undefined
                    : values.duration
                      ? addMinutesToTime(values.time, values.duration)
                      : values.time
                }
                onValue={val => {
                  if (!val || !values.time) return;
                  onChange({ ...values, duration: calculateTimeDifference(val, values.time) });
                }}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Host" className="col-span-3">
              <BubbleHintSelect
                block
                allowUndefined
                options={users.map(u => u._id)}
                labelExtractor={u =>
                  makeFullName(usersMap[u]?.firstName, usersMap[u]?.lastName) || 'Unknown User'
                }
                readOnly={readOnly}
                value={values.host}
                onValue={val => onChange({ ...values, host: val })}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Rental" className="col-span-3">
              <BubbleHintSelect
                block
                allowUndefined
                options={resourcesList.map(u => u._id)}
                labelExtractor={u => resourcesMap[u]?.name || 'Unknown Resource'}
                readOnly={readOnly}
                value={values.resource}
                onValue={val => onChange({ ...values, resource: val })}
              />
            </BubbleFormGroup>
          </fieldset>
        )}
      </div>
    </div>
  );
}
